import {
  MetricOverrideParams,
  useGetInitiativeUniversalTrackersQuery,
  useUpdateMetricOverridesMutation,
} from '@api/initiative-universal-trackers';
import { addSiteError } from '@g17eco/slices/siteAlertsSlice';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { MetricOverrideForm } from './MetricOverrideForm';
import { getInitialForm, getStorageAIProcessingData } from './utils';
import { MetricOverrideProps } from '../types';
import { useMemo, useState } from 'react';
import { ExcludeAIProcessingWarning } from './ExcludeAIProcessingWarning';
import { AiProcessingValue } from '@g17eco/types/ai';

export const MetricOverrideContainer = (props: MetricOverrideProps) => {
  const { handleReload, selectedQuestions, initiativeId, handleCloseModal } = props;
  const selectedUtrIds = selectedQuestions.map((q) => q._id);

  const { data: initiativeUtrs, isFetching } = useGetInitiativeUniversalTrackersQuery(initiativeId, {
    skip: !initiativeId || selectedQuestions.length === 0,
  });

  const initiativeUtrMap = useMemo(() => {
    return new Map<string, InitiativeUniversalTracker>(
      (initiativeUtrs ?? []).map((utr: InitiativeUniversalTracker) => [utr.universalTrackerId, utr]),
    );
  }, [initiativeUtrs]);

  const [update, { isLoading: isUpdating }] = useUpdateMetricOverridesMutation();
  const [pendingUpdate, setPendingUpdate] = useState<Pick<MetricOverrideParams, 'utrvConfig' | 'aiProcessing'>>();

  const initialForm = getInitialForm({ initiativeUtrMap, selectedQuestions });

  const isLoading = isFetching || isUpdating;

  const onClickUpdate = (data: Pick<MetricOverrideParams, 'utrvConfig' | 'aiProcessing'>) => {
    const isSkippedWarning = getStorageAIProcessingData(initiativeId).notShowAgain;
    
    if (data.aiProcessing === AiProcessingValue.Exclude && !isSkippedWarning) {
      setPendingUpdate(data);
    } else {
      handleUpdate(data);
    }
  };

  const handleUpdate = (data: Pick<MetricOverrideParams, 'utrvConfig' | 'aiProcessing'>) => {
    update({ initiativeId, utrIds: selectedUtrIds, ...data })
      .unwrap()
      .then(() => {
        handleReload({ reloadSurvey: true, closeModal: selectedQuestions.length === 1 });
      })
      .catch((err) => {
        addSiteError(err);
        handleReload();
      });
  };

  return (
    <>
      <MetricOverrideForm
        key={JSON.stringify(initialForm)}
        isLoading={isLoading}
        handleUpdate={onClickUpdate}
        handleCloseModal={handleCloseModal}
        initialForm={initialForm}
        isMultipleUpdate={selectedUtrIds.length > 1}
      />
      <ExcludeAIProcessingWarning
        isOpen={!!pendingUpdate}
        onClose={() => setPendingUpdate(undefined)}
        onConfirm={() => {
          if (pendingUpdate) {
            handleUpdate(pendingUpdate);
            setPendingUpdate(undefined);
          }
        }}
        initiativeId={initiativeId}
      />
    </>
  );
};
